// "Team now" (WEB_PLAN.md §5.3): who is in the field, with the iOS RepRow look: presence avatar, name ("You" chip),
// the location line ("Online · 4 minutes ago · as of 2:14 PM" / "No location yet"), the newest activity ("On-Site
// Visit · Contacted · 19 minutes ago", RepPeekCard) and today's VISITS / CONTACTS columns. Managers open the rep's
// day; a rep sees only their own row, titled "Today". The iPhone uploads GPS about every 5 minutes, so the position's
// clock time is spelled out, in amber once the rep is no longer Online (no new point for 15 minutes; the phone records
// one only when the rep moves, so a rep parked in a store goes quiet too).

import { ChevronRight, Users } from 'lucide-react';
import { Link } from 'react-router';
import { fullName } from '@/domain/util';
import { formatElapsed, formatGrouped } from '@/domain/format';
import { Avatar, Chip, EmptyState, SectionCard, cn } from '@/ui';
import { ActivityGlyph } from './ActivityGlyph';
import { POSITION_STALE_MS, positionAge, presenceLine, presenceSummary, roleTitle, type TeamNowRow } from './dashboardModel';

const STALE_HINT = `No new location for ${POSITION_STALE_MS / 60_000} minutes or more. The phone sends one when the rep moves.`;

/** Rows shown before "See all" (the Team page has everyone). */
const MAX_ROWS = 8;

export interface TeamNowCardProps {
  rows: readonly TeamNowRow[];
  now: Date;
  /** Managers: rows link to `/team/:id` and "See all" to `/team`. */
  linkToTeam: boolean;
  /** A rep's own row ("Today"): the empty state talks about them, not about reps in general. */
  self?: boolean;
  title?: string;
  className?: string;
}

export function TeamNowCard({ rows, now, linkToTeam, self = false, title = 'Team now', className }: TeamNowCardProps) {
  const shown = rows.slice(0, MAX_ROWS);
  const more = rows.length - shown.length;
  return (
    <SectionCard
      title={title}
      description={rows.length > 0 && linkToTeam ? presenceSummary(rows) : undefined}
      actionLabel={linkToTeam ? (more > 0 ? `See all ${formatGrouped(rows.length)}` : 'Team') : undefined}
      actionHref={linkToTeam ? '/team' : undefined}
      padding="none"
      bodyClassName="overflow-hidden"
      className={className}
    >
      {rows.length === 0 ? (
        <EmptyState
          compact
          icon={<Users />}
          title={self ? 'Your profile is still syncing' : 'Nobody in the field yet'}
          message={
            self
              ? "Your location and today's visits show here once your profile arrives from your company."
              : "Reps show up here with their location and today's visits once they start logging in the Ground Team app."
          }
        />
      ) : (
        <ul className="divide-y divide-line">
          {shown.map((row) => (
            <li key={row.user.id}>
              <RepNowRow row={row} now={now} href={linkToTeam ? `/team/${row.user.id}` : undefined} />
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function RepNowRow({ row, now, href }: { row: TeamNowRow; now: Date; href?: string }) {
  const name = fullName(row.user) || 'Unnamed';
  const subtitle = row.user.title.trim() === '' ? roleTitle(row.user) : `${row.user.title.trim()} · ${roleTitle(row.user)}`;
  const last = row.lastActivity;
  const age = positionAge(row.lastSeenAt, now);
  const location = age ? `${presenceLine(row, now)}, ${age.text}` : presenceLine(row, now);
  const body = (
    <>
      <Avatar name={name} colorHex={row.user.colorHex} size="lg" presence={row.presence} label={null} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-fg">{name}</span>
          {row.isMe && (
            <Chip size="xs" color="#102A5E">
              You
            </Chip>
          )}
        </div>
        <span className="truncate text-xs text-fg-muted">{subtitle}</span>
        <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-fg-muted">
          <span
            aria-hidden
            className={cn(
              'size-2 shrink-0 rounded-full',
              row.presence === 'online' ? 'bg-green' : row.presence === 'away' ? 'bg-amber' : 'bg-grey',
            )}
          />
          <span className={cn('truncate', !row.lastSeenAt && 'text-fg-subtle')}>{presenceLine(row, now)}</span>
          {age && (
            <span
              title={age.stale ? STALE_HINT : undefined}
              className={cn('shrink-0 tabular-nums', age.stale ? 'text-warning-text' : 'text-fg-subtle')}
            >
              · {age.text}
            </span>
          )}
        </span>
        {last ? (
          <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-fg-muted">
            <ActivityGlyph type={last.type} color={last.tint} size={18} />
            <span className="truncate">
              <span className="sr-only">Recent activity: </span>
              {last.title}
            </span>
            <span className="shrink-0 text-fg-subtle">· {formatElapsed(last.at, now)}</span>
          </span>
        ) : (
          <span className="mt-0.5 text-[11px] text-fg-subtle">No activity logged yet</span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-4">
        <CountColumn value={row.visits} title="Visits" />
        <CountColumn value={row.contacts} title="Contacts" />
      </div>
      {href && <ChevronRight aria-hidden className="size-4 shrink-0 text-fg-subtle" />}
    </>
  );
  const classes = 'flex min-w-0 items-center gap-3 px-4 py-3';
  if (!href) return <div className={classes}>{body}</div>;
  return (
    <Link
      to={href}
      className={cn(classes, 'transition-colors hover:bg-surface-2')}
      aria-label={`${name}${row.isMe ? ' (you)' : ''}, ${subtitle}, ${location}. Today: ${row.visits} visits, ${row.contacts} contacts.${
        last ? ` Recent activity: ${last.title}, ${formatElapsed(last.at, now)}.` : ' No activity logged yet.'
      }`}
    >
      {body}
    </Link>
  );
}

/** RepRow.countColumn: the number over a small caps title ("12" / "VISITS"), labelled for "today". */
function CountColumn({ value, title }: { value: number; title: string }) {
  return (
    <div className="flex min-w-10 flex-col items-center">
      <span className="font-rounded text-sm font-bold text-fg tabular-nums">{formatGrouped(value)}</span>
      <span className="text-[9px] font-semibold tracking-[0.04em] text-fg-muted uppercase">
        {title}
        <span className="sr-only"> today</span>
      </span>
    </div>
  );
}
